import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

type ReturnType = boolean;

export type Query = 'up' | 'down' | 'between' | 'only';

export type Value = Breakpoint | number;

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const breakpointValues: Record<Breakpoint, number> = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600
};

function useBreakpoint() {
  const [screens, setScreens] = useState<Record<Breakpoint, boolean>>({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
    xxl: false
  });

  useEffect(() => {
    const mediaQueryLists: { [key: string]: MediaQueryList } = {};
    const handlers: { [key: string]: (e: MediaQueryListEvent) => void } = {};

    Object.entries(breakpointValues).forEach(([breakpoint, width]) => {
      const mediaQuery = `(min-width: ${width}px)`;
      mediaQueryLists[breakpoint] = window.matchMedia(mediaQuery);
      
      handlers[breakpoint] = (e: MediaQueryListEvent) => {
        setScreens(prev => ({
          ...prev,
          [breakpoint]: e.matches
        }));
      };

      mediaQueryLists[breakpoint].addListener(handlers[breakpoint]);
      
      // Set initial values
      setScreens(prev => ({
        ...prev,
        [breakpoint]: mediaQueryLists[breakpoint].matches
      }));
    });

    return () => {
      Object.entries(mediaQueryLists).forEach(([breakpoint, mediaQueryList]) => {
        mediaQueryList.removeListener(handlers[breakpoint]);
      });
    };
  }, []);

  return screens;
}

export function useResponsive(query: Query, start?: Value, end?: Value): ReturnType {
  const screens = useBreakpoint();
  
  const breakpoints: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
  const startIndex = typeof start === 'number' ? -1 : breakpoints.indexOf(start as Breakpoint);

  if (query === 'up') {
    if (typeof start === 'number') {
      return window.innerWidth >= start;
    }
    return !!screens[start as Breakpoint];
  }

  if (query === 'down') {
    if (typeof start === 'number') {
      return window.innerWidth < start;
    }
    const nextBreakpoint = breakpoints[startIndex + 1];
    return !screens[nextBreakpoint];
  }

  if (query === 'between') {
    if (typeof start === 'number' && typeof end === 'number') {
      return window.innerWidth >= start && window.innerWidth < end;
    }
    const endIndex = typeof end === 'number' ? -1 : breakpoints.indexOf(end as Breakpoint);
    return !!screens[start as Breakpoint] && !screens[breakpoints[endIndex + 1]];
  }

  // 'only'
  if (typeof start === 'number') {
    const nextBreakpoint = breakpoints.find(bp => breakpointValues[bp] > start);
    return window.innerWidth >= start && (!nextBreakpoint || !screens[nextBreakpoint]);
  }
  const nextBreakpoint = breakpoints[startIndex + 1];
  return !!screens[start as Breakpoint] && !screens[nextBreakpoint];
}

// ----------------------------------------------------------------------

export function useWidth(): Breakpoint {
  const screens = useBreakpoint();
  const breakpoints: Breakpoint[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
  
  return breakpoints.find(bp => screens[bp]) || 'xs';
}
